import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Badge,
  Snackbar,
  Alert,
  Chip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import FolderIcon from '@mui/icons-material/Folder';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

const drawerWidth = 240;

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { text: 'ICV Contractors', icon: <PeopleIcon />, path: '/contractors' },
  { text: 'Projects', icon: <FolderIcon />, path: '/projects' },
  { text: 'Time Entries', icon: <AccessTimeIcon />, path: '/time-entries' },
  { text: 'Invoices', icon: <ReceiptIcon />, path: '/invoices' },
  { text: 'Payments', icon: <PaymentIcon />, path: '/payments' },
];

const Layout: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [alertAnchorEl, setAlertAnchorEl] = useState<null | HTMLElement>(null);
  const [budgetAlerts, setBudgetAlerts] = useState<any[]>([]);
  const [alertOpen, setAlertOpen] = useState(false);
  const [currentAlert, setCurrentAlert] = useState<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();


  const fetchBudgetAlerts = useCallback(async () => {
    try {
      const response = await api.get('/budget-alerts');
      setBudgetAlerts(response.data.alerts || []);
      
      // Show snackbar for critical alerts
      const criticalAlerts = response.data.alerts.filter((a: any) => a.severity === 'critical');
      if (criticalAlerts.length > 0 && !alertOpen) {
        setCurrentAlert(criticalAlerts[0]);
        setAlertOpen(true);
      }
    } catch (error) {
      console.error('Failed to fetch budget alerts:', error);
    }
  }, [alertOpen]);

  useEffect(() => {
    if (user?.role === 'ADMIN') {
      fetchBudgetAlerts();
      const interval = setInterval(fetchBudgetAlerts, 60000); // Check every minute
      return () => clearInterval(interval);
    }
  }, [user, fetchBudgetAlerts]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const drawer = (
    <Box>
      <Toolbar sx={{ backgroundColor: '#FF6B35' }}>
        <Typography 
          variant="h6" 
          noWrap 
          component="div"
          sx={{ 
            fontWeight: 600,
            color: '#ffffff',
          }}
        >
          CRM ICV Group Pay
        </Typography>
      </Toolbar>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                console.log(`[Layout] ====== NAVIGATION CLICK ======`);
                console.log(`[Layout] Menu Item: ${item.text}`);
                console.log(`[Layout] Target Path: ${item.path}`);
                console.log(`[Layout] Current Path: ${location.pathname}`);
                console.log(`[Layout] About to navigate...`);
                // Force navigation using window.location as fallback
                if (location.pathname !== item.path) {
                  navigate(item.path, { replace: false });
                  // Fallback: if navigate doesn't work, use window.location
                  setTimeout(() => {
                    if (window.location.pathname !== item.path) {
                      console.warn(`[Layout] Navigate failed, using window.location fallback`);
                      window.location.href = item.path;
                    }
                  }, 100);
                }
                setMobileOpen(false);
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          backgroundColor: '#FF6B35',
          background: 'linear-gradient(135deg, #FF6B35 0%, #F97316 100%)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600 }}>
              Administrator Portal
            </Typography>
            <Chip 
              label="ADMIN" 
              size="small" 
              sx={{ 
                backgroundColor: '#ffffff',
                color: '#FF6B35',
                fontWeight: 700,
                fontSize: '0.7rem',
              }} 
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {user?.role === 'ADMIN' && budgetAlerts.length > 0 && (
              <IconButton onClick={(e) => setAlertAnchorEl(e.currentTarget)} color="error">
                <Badge badgeContent={budgetAlerts.length} color="error">
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            )}
            <Typography variant="body2">{user?.firstName} {user?.lastName}</Typography>
            <IconButton onClick={handleMenuClick} size="small">
              <Avatar sx={{ width: 32, height: 32 }}>
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </Avatar>
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
            <Menu
              anchorEl={alertAnchorEl}
              open={Boolean(alertAnchorEl)}
              onClose={() => setAlertAnchorEl(null)}
            >
              {budgetAlerts.map((alert, index) => (
                <MenuItem
                  key={index}
                  onClick={() => {
                    navigate('/projects');
                    setAlertAnchorEl(null);
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight="bold" color={alert.severity === 'critical' ? 'error' : 'warning.main'}>
                      {alert.projectName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {alert.message}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: drawerWidth,
              backgroundColor: '#FFF5F0',
              borderRight: '2px solid #FFE0D6',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: '#f5f5f5',
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
      <Snackbar
        open={alertOpen}
        autoHideDuration={6000}
        onClose={() => setAlertOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setAlertOpen(false)}
          severity={currentAlert?.severity === 'critical' ? 'error' : 'warning'}
          sx={{ width: '100%' }}
        >
          {currentAlert?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Layout;

